export type PaymentStatus = 'paid' | 'unpaid' | 'partially_paid';

/**
 * Tolérance de règlement : 1 millime. Un écart ≤ 0,001 DT provient d'arrondis
 * (répartitions RAS, imports, saisies) — le document est considéré réglé
 * plutôt que de rester « partiellement payé » pour un millime impayable.
 */
export const PAYMENT_EPSILON = 0.001;

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Tolérance de règlement d'un document : son TIMBRE FISCAL.
 *
 * Le timbre (1 DT) n'existe que sur la facture — jamais sur le BL. Un BL soldé
 * transformé en facture laissait donc une facture « partiellement payée » à
 * 1 DT près, sur CHAQUE vente encaissée au comptoir. Décision métier (Khalil,
 * 06/08/2026) : le timbre ne doit jamais empêcher une facture d'être soldée —
 * la facture suit son document d'origine. Une facture réellement impayée reste
 * impayée (le montant à encaisser, lui, inclut toujours le timbre).
 *
 * ⚠ `stampAmount` NÉGATIF : 30 factures importées de l'ancien système s'en
 * servaient comme colonne d'ajustement (jusqu'à −749 DT). Une tolérance
 * négative n'aurait aucun sens, et une tolérance énorme solderait une facture
 * impayée — on ne retient donc que la valeur positive.
 */
export function settlementToleranceFor(doc: { stampAmount?: number | null }): number {
  const stamp = Number(doc.stampAmount);
  return Number.isFinite(stamp) && stamp > 0 ? round3(stamp) : 0;
}

export function computePaymentStatus(
  totalDue: number,
  allocatedAmount: number,
  /** Écart toléré au-delà de l'arrondi — voir settlementToleranceFor. */
  tolerance = 0,
): PaymentStatus {
  // Comparaisons sur montants arrondis au millime : en flottant brut,
  // 348.368 + 0.001 < 348.369 et un doc à un millime près restait « partiel ».
  const paid = round3(allocatedAmount);
  // Un document sur lequel RIEN n'a été encaissé est impayé, quelle que soit la
  // tolérance : sans cette garde, une facture au timbre seul naîtrait « payée ».
  if (paid <= PAYMENT_EPSILON) return 'unpaid';
  const remaining = round3(totalDue - allocatedAmount);
  if (remaining <= settledThreshold(tolerance)) return 'paid';
  return 'partially_paid';
}

/** Seuil en deçà duquel un reste ne fait plus créance. */
export function settledThreshold(tolerance = 0): number {
  const tol = Number(tolerance);
  return Number.isFinite(tol) && tol > PAYMENT_EPSILON ? round3(tol) : PAYMENT_EPSILON;
}

export function sumAllocations(
  allocations: { allocatedAmount: number }[] | undefined | null,
): number {
  if (!allocations) return 0;
  return allocations.reduce((s, a) => s + (Number(a.allocatedAmount) || 0), 0);
}
